import { useMemo } from 'react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useData } from '@/contexts/data'

interface PostTerritoriesProps {
  territoryIds: string[]
}

export function PostTerritories({ territoryIds }: PostTerritoriesProps) {
  const { territories } = useData()
  const isMock = territoryIds.some(id => id.startsWith('territory-'))

  const idToName = useMemo(() => {
    const map = new Map<string, string>()
    territories.forEach((t) => map.set(t.id, t.name))
    return map
  }, [territories])

  const territoryNames = useMemo(() => {
    if (isMock) return territoryIds.map(() => 'Território')
    if (territoryIds.length === 0) return []
    return territoryIds
      .map(id => idToName.get(id))
      .filter(Boolean) as string[]
  }, [territoryIds, idToName, isMock])

  if (territoryNames.length === 0) {
    return null
  }

  const displayedTerritories = territoryNames.slice(0, 2)
  const remainingCount = territoryNames.length - 2
  const allTerritoriesText = territoryNames.join(', ')

  return (
    <div className="flex items-center gap-2">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2">
              {displayedTerritories.map((territory, index) => (
                <div key={index} className="bg-gray-800 text-white px-2 py-0.5 rounded-full text-xs">
                  {territory}
                </div>
              ))}
              {remainingCount > 0 && (
                <div className="bg-gray-800 text-white px-2 py-0.5 rounded-full text-xs">
                  +{remainingCount}
                </div>
              )}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="!bg-gray-800 !text-white border-gray-800 rounded-lg py-1.5 px-3"
          >
            <p>{allTerritoriesText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
